import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, RefreshCcw, BadgePercent, QrCode, ArrowLeft, ChevronRight } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import DynamicList from "../../components/dynamic/List";
import { ApiService } from "../../services/api";
import { isYesToolsHostname } from "../../utils/yesTools";

type MenuCategory = {
  id: string;
  name: string;
};

type MenuProduct = {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  basePrice?: number | null;
  priceListPrice?: number | null;
  finalPrice?: number | null;
  hasCampaign?: boolean;
  campaignName?: string | null;
  category?: { id: string; name: string };
};

const formatPrice = (price?: number | null) => {
  if (price === null || price === undefined || Number.isNaN(price)) return "-";

  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  }).format(price);
};

function CafeProductCard({ product, viewMode }: { product: MenuProduct; viewMode?: string }) {
  if (viewMode === "grid") {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden hover:bg-gray-100 transition-colors h-full">
        <div className="aspect-square bg-white border-b border-gray-200 overflow-hidden">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 font-semibold text-2xl">
              {product.name.slice(0, 1)}
            </div>
          )}
        </div>

        <div className="p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{product.name}</h3>
            <p className="mt-1 text-xs text-gray-500 line-clamp-2">
              {product.description || product.category?.name || "Cafe ürünü"}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            {product.basePrice && product.basePrice !== product.finalPrice ? (
              <div className="text-xs text-gray-400 line-through">{formatPrice(product.basePrice)}</div>
            ) : null}
            <div className="text-lg font-bold text-gray-900">{formatPrice(product.finalPrice)}</div>
          </div>

          {product.hasCampaign && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <BadgePercent className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{product.campaignName || "Aktif kampanya uygulanıyor"}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-colors">
      <div className="flex gap-4">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-white border border-gray-200 overflow-hidden flex-shrink-0">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 font-semibold text-xl">
              {product.name.slice(0, 1)}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {product.description || product.category?.name || "Cafe ürünü"}
            </p>
            {product.hasCampaign && (
              <div className="mt-3 inline-flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <BadgePercent className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{product.campaignName || "Aktif kampanya uygulanıyor"}</span>
              </div>
            )}
          </div>

          <div className="w-full md:w-52 bg-white border border-gray-200 rounded-lg p-3">
            {product.basePrice && product.basePrice !== product.finalPrice ? (
              <div className="text-xs text-gray-400 line-through">{formatPrice(product.basePrice)}</div>
            ) : null}
            <div className="text-xl font-bold text-gray-900">{formatPrice(product.finalPrice)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CafeCategoryPanel({
  categoryId,
}: {
  categoryId?: string;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const actions = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setRefreshKey((prev) => prev + 1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          <RefreshCcw className="w-4 h-4" />
          Yenile
        </button>
      </div>
    ),
    []
  );

  const fetcher = useCallback(
    async (_page: number, searchTerm?: string) => {
      const response = await ApiService.getCafeMenuCatalog({
        categoryId,
        search: searchTerm || undefined,
        limit: 200,
        offset: 0,
      });

      return {
        data: response?.data?.products || [],
        meta: {
          page: 1,
          totalPages: 1,
          totalItems: (response?.data?.products || []).length,
          limit: 200,
        },
      };
    },
    [categoryId]
  );

  return (
    <DynamicList
      type="list"
      header={false}
      title="Cafe Menüsü"
      path={`cafe-menu-${categoryId || "all"}`}
      fetcher={fetcher}
      refreshKey={refreshKey}
      isSearch
      isPagination={false}
      showToggle
      actions={actions}
      emptyMessage="Bu kategoride gösterilecek ürün bulunamadı."
      errorMessage="Cafe menüsü yüklenirken bir hata oluştu."
      item={(product: MenuProduct, viewMode?: string) => (
        <CafeProductCard key={product.id} product={product} viewMode={viewMode} />
      )}
    />
  );
}

type CafeWorld = "menu" | "kirtasiye";

// Muhasebedeki kategori adlarından dünya ayrımı: Kırtasiye + Kitap = kırtasiye,
// kalanı (Tatlı/Tuzlu Yiyecek, İçecek...) = menü. Kaynak muhasebe, eşleme bizde.
const isStationeryCategory = (name?: string) => {
  const normalized = String(name || "").toLocaleLowerCase("tr-TR");
  return normalized.includes("kırtasiye") || normalized.includes("kitap");
};

type CampaignSummary = { id: string; title: string; benefit: string };

const summarizeCampaigns = (response: any): CampaignSummary[] => {
  const candidates = [
    response?.data?.campaigns,
    response?.data?.rows,
    response?.data?.data?.campaigns,
    response?.data?.data,
    response?.data,
  ];
  const list = candidates.find((item: any) => Array.isArray(item)) || [];
  return (list as any[])
    .filter((c) => c && c.isActive !== false && c.active !== false)
    .slice(0, 10)
    .map((c, i) => {
      const percentage = Number(c.percentage ?? c.discountPercentage ?? c.discountRate);
      const amount = Number(c.amount ?? c.discountAmount);
      const fixedPrice = Number(c.fixedPrice);
      const benefit =
        Number.isFinite(percentage) && percentage > 0
          ? `%${percentage} indirim`
          : Number.isFinite(amount) && amount > 0
            ? `${formatPrice(amount)} indirim`
            : Number.isFinite(fixedPrice) && fixedPrice > 0
              ? `Sabit fiyat ${formatPrice(fixedPrice)}`
              : String(c.description || "").trim() || "Kampanya";
      return {
        id: String(c.id || i),
        title: String(c.name || c.title || "Kampanya"),
        benefit,
      };
    });
};

function WorldCard({
  emoji,
  title,
  subtitle,
  count,
  onClick,
}: {
  emoji: string;
  title: string;
  subtitle: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:bg-gray-50 transition-colors flex flex-col gap-3"
    >
      <span className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center text-2xl">{emoji}</span>
      <span>
        <span className="block text-base font-bold text-gray-900">{title}</span>
        <span className="block text-xs text-gray-500 mt-0.5">{subtitle}</span>
        <span className="block text-[11px] text-gray-400 mt-1">{count} kategori</span>
      </span>
    </button>
  );
}

export default function CafeMenuPage() {
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [world, setWorld] = useState<CafeWorld | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await ApiService.getCafeMenuCatalog({
        limit: 200,
        offset: 0,
      });

      setCategories(Array.isArray(response?.data?.categories) ? response.data.categories : []);
    } catch (err: any) {
      console.error("Cafe menüsü kategorileri yüklenirken hata:", err);
      setError(err?.message || "Cafe menüsü yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isYesToolsDomain) return;
    loadCategories();
    // Kampanya şeridi ana ekranı bloklamaz; hata olursa şerit gizli kalır
    ApiService.getCafeCampaigns()
      .then((response) => setCampaigns(summarizeCampaigns(response)))
      .catch(() => {});
  }, [isYesToolsDomain, loadCategories]);

  const menuCategories = useMemo(
    () => categories.filter((category) => !isStationeryCategory(category.name)),
    [categories]
  );
  const stationeryCategories = useMemo(
    () => categories.filter((category) => isStationeryCategory(category.name)),
    [categories]
  );

  const buildTabs = (list: MenuCategory[]): TabItemProps[] =>
    list.map((category) => ({
      type: "custom",
      title: category.name,
      path: category.id,
      tabId: category.id,
      customComponent: <CafeCategoryPanel categoryId={category.id} />,
    }));

  if (!isYesToolsDomain) {
    return <Navigate to="/dashboard" replace />;
  }

  if (world) {
    const label = world === "menu" ? "Menü" : "Kırtasiye";
    const list = world === "menu" ? menuCategories : stationeryCategories;

    return (
      <div className="flex flex-col h-full">
        <div className="px-4 pt-4 md:px-6">
          <button
            onClick={() => setWorld(null)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Kafe
          </button>
        </div>
        {list.length === 0 ? (
          <div className="p-4 md:p-6">
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
              Bu bölümde henüz kategori yok.
            </div>
          </div>
        ) : (
          <TabPage title={label} tabs={buildTabs(list)} />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6 max-w-2xl mx-auto w-full">
      <div>
        <h1 className="text-xl font-bold text-white">Kafe</h1>
        <p className="text-sm text-white/60 mt-1">Kiosk ödemesi, menü ve kırtasiye tek yerde.</p>
      </div>

      {/* Birincil eylem: tepsi kioskta okundu, sıra ödemede */}
      <button
        onClick={() => navigate("/kiosk-pay")}
        className="w-full rounded-2xl bg-blue-700 hover:bg-blue-800 transition-colors text-white p-5 flex items-center gap-4 text-left shadow-lg"
      >
        <span className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
          <QrCode className="w-7 h-7" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-lg font-bold">QR Okut & Öde</span>
          <span className="block text-sm text-white/70 mt-0.5">
            Kiosk ekranındaki karekodu okutun, ödemenizi tamamlayın.
          </span>
        </span>
        <ChevronRight className="w-5 h-5 text-white/60 shrink-0" />
      </button>

      {campaigns.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-white/50 mb-2">Kampanyalar</p>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="min-w-[200px] max-w-[240px] bg-white border border-gray-200 rounded-xl p-3 shrink-0"
              >
                <div className="flex items-center gap-2">
                  <BadgePercent className="w-4 h-4 text-orange-600 shrink-0" />
                  <p className="text-sm font-semibold text-gray-800 truncate">{campaign.title}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{campaign.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Katalog yüklenemese bile ödeme butonu ayakta kalır; hata bu alanla sınırlı */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={loadCategories}
            className="mt-3 px-4 py-2 rounded-lg bg-white border border-red-200 text-sm font-semibold text-red-700 hover:bg-red-100 transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <WorldCard
            emoji="🍽️"
            title="Menü"
            subtitle="Tatlı, tuzlu, içecek"
            count={menuCategories.length}
            onClick={() => setWorld("menu")}
          />
          <WorldCard
            emoji="✏️"
            title="Kırtasiye"
            subtitle="Kitap, defter, kalem"
            count={stationeryCategories.length}
            onClick={() => setWorld("kirtasiye")}
          />
        </div>
      )}
    </div>
  );
}
